import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { breederTransportOptionsQueryDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isNumeric } from 'dpl/shared/form/utils';
import { MOBILE_TAB_KEYS } from 'dpl/components/BreederProfile/utils/constants';
import { FILTER_LOCATION_NAME } from 'dpl/constants/query_params';
import Icon from 'dpl/common/components/Icon';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';
import SmartImage from 'dpl/components/SmartImage';
import { makeSequence } from 'dpl/shared/utils';

import { useBreederProfileData } from '../../hooks/useBreederProfileData';
import useBreederProfileTabs from '../../../hooks/useBreederProfileTabs';

export function getLocationDescription({ location }) {
  const { distance, city_state: cityState } = location || {};
  const isNearby = isNumeric(distance) && distance > 0 && distance < 250;

  return [
    cityState,
    isNearby && `${distance} ${distance === 1 ? 'mile' : 'miles'} away`
  ]
    .filter(Boolean)
    .join(' · ');
}

function OverviewCardItemSkeleton() {
  return (
    <li className="flex items-center gap-8">
      <SkeletonBlock className="br-100" width="20px" height="20px" />
      <SkeletonBlock className="font-14" width="12em" height="1em" />
    </li>
  );
}

function OverviewCardItem({ isLoading, iconName, description }) {
  if (isLoading) return <OverviewCardItemSkeleton />;

  return (
    <li className="flex items-center gap-8 secondary">
      <Icon name={iconName} className="secondary" width="20px" height="20px" />
      {description}
    </li>
  );
}

OverviewCardItem.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  iconName: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired
};

export default function OverviewCard() {
  const {
    server: { breederProfileId, testimonialsCount },
    queryParams: { [FILTER_LOCATION_NAME]: locName }
  } = useSelector(({ server, queryParams }) => ({ server, queryParams }));

  const { breeder, isFetchingBreeder } = useBreederProfileData();
  const { replaceHash } = useBreederProfileTabs();

  const {
    isFetching: isFetchingLocation,
    value: {
      data: { location }
    }
  } = useResourceQuery(
    breederTransportOptionsQueryDefinition(breederProfileId, { locName })
  );

  const overviewItems = useMemo(() => {
    const locationDescription = getLocationDescription({ location });

    return [
      (isFetchingLocation || !isEmpty(locationDescription)) && {
        isLoading: isFetchingLocation,
        iconName: 'fetch-location',
        description: locationDescription
      },
      !isEmpty(breeder.meet_up_availability_sentence) && {
        isLoading: false,
        iconName: 'fetch-transportation-car',
        description: breeder.meet_up_availability_sentence
      },
      !isEmpty(breeder.response_time_description) && {
        isLoading: false,
        iconName: 'fetch-message',
        description: breeder.response_time_description
      }
    ].filter(Boolean);
  }, [isFetchingLocation, breeder, location]);

  if (isFetchingBreeder) {
    return (
      <div className="container-xxl mb8">
        <div className="flex flex-column gap-16 ba br4 b--default pv4 ph4 font-14">
          <div className="flex items-center gap-16">
            <SkeletonBlock className="br2" height="96px" width="96px" />
            <div className="flex-auto flex-basis-0">
              <SkeletonBlock
                className="font-24 mb2"
                height="1.2em"
                width="70%"
              />
              <SkeletonBlock
                className="font-14 mb2"
                height="1.4em"
                width="80%"
              />
              <SkeletonBlock className="font-14" height="1.4em" width="50%" />
            </div>
          </div>
          <ul className="flex flex-column gap-4">
            {makeSequence(3).map(index => (
              <OverviewCardItemSkeleton key={index} />
            ))}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="OverviewCard container-xxl mb8">
      <div className="flex flex-column gap-16 ba br4 b--default pv4 ph4 font-14 lh-body secondary">
        <div className="flex items-center gap-16">
          {breeder.avatar_profile_photo_url && (
            <SmartImage
              Tag="div"
              src={breeder.avatar_profile_photo_url}
              className="bg-center cover h-100 w-100 br3"
              width="96px"
              height="96px"
            />
          )}
          <div className="flex-auto flex-basis-0">
            <p className="font-24 font-serif fw-medium default-color truncate">
              {breeder.display_name}
            </p>
            <p className="font-16 lh-normal">of {breeder.name}</p>
            {testimonialsCount > 0 && (
              <button
                type="button"
                className="font-16 underline stone-700 mt1"
                onClick={() => replaceHash(MOBILE_TAB_KEYS.STORIES)}
              >
                {testimonialsCount} owner{' '}
                {testimonialsCount === 1 ? 'review' : 'reviews'}
              </button>
            )}
          </div>
        </div>
        {!isEmpty(overviewItems) && (
          <ul className="flex flex-column gap-4">
            {overviewItems.map(({ isLoading, iconName, description }) => (
              <OverviewCardItem
                key={description}
                isLoading={isLoading}
                iconName={iconName}
                description={description}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
